import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";

interface Job {
  id: string;
  jobDescription: string;
  jobPosition: string;
  education: string;
  experience: number;
  salary?: number;
  createdAt: any;
}

const formatDate = (timestamp: any) => {
  if (!timestamp) return "N/A";
  return new Date(timestamp.seconds * 1000).toLocaleString();
};

const JobDetails: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) return;

    const fetchJob = async () => {
      try {
        const db = getFirestore(firebaseApp);
        const docRef = doc(db, "hrJobs", jobId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setJob({ id: snap.id, ...snap.data() } as Job);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId]);

  if (loading) {
    return <div className="text-center py-10">Loading job details...</div>;
  }

  if (!job) {
    return (
      <div className="text-center py-10">
        Job not found.
        <Link to="/hr/my-jobs" className="text-red-700 hover:underline">
          Back to My Jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 capitalize">
          {job.jobPosition}
        </h1>
        <Link to="/hr/my-jobs" className="text-red-700 hover:underline">
          ← Back to My Jobs
        </Link>
      </div>

      <div className="bg-white p-6 border rounded-lg shadow-sm">
        <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-6">
          <p>
            <strong>Posted On:</strong> {formatDate(job.createdAt)}
          </p>
          <p>
            <strong>Education:</strong> {job.education}
          </p>
          <p>
            <strong>Experience:</strong> {job.experience} years
          </p>
          {job.salary && (
            <p>
              <strong>Salary:</strong> ${job.salary.toLocaleString()}
            </p>
          )}
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Job Description
        </h2>
        <div className="prose max-w-none" style={{ whiteSpace: "pre-wrap" }}>
          {job.jobDescription}
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
